import { useEffect, useId, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, ArrowRight, ShieldCheck, X as XIcon, Zap } from 'lucide-react';

import { Spinner } from '@/components/ui/spinner';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { ProvenanceChip } from '@/components/ui/provenance-chip';
import {
  useOverrideTaxonomyQuery,
  useCaptureOverrideMutation,
  type OverrideTaxonomyTag,
  type CaptureOverrideResult,
} from '@/lib/api/overrides';
import { strategyHintForTag } from '@/lib/api/reevaluations';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/utils/cn';

/**
 * T-0113 / E04-F02 — Structured override modal.
 *
 * Visual contract:
 *   - `design_handoff_caliber_v2/prototype/modals.js` → `Modals.openOverride`.
 *   - README §8 pattern 3 (Override + re-eval flow).
 *
 * Behavior contract:
 *   - Tag picker (chip row) sourced from `GET /api/v1/config/override-taxonomy`
 *     so the v1 taxonomy isn't hardcoded in the UI. Server validates the
 *     `reason_tag` against the same enum.
 *   - Free-text rationale with a soft validation gate at ≥10 characters
 *     (server gate is 10 chars per `MIN_RATIONALE_CHARS`; the modal
 *     surfaces both the char count + "≥1 sentence" hint).
 *   - Diff view shows current (AI / prior) value vs the new value. The
 *     parent provides the prior + draft new value as opaque structured
 *     props so this modal can host any register entry type.
 *   - No-auto-accept banner when the entry is `baselineForming === true`
 *     (E02-F06 — baseline-forming items don't get auto-applied even at
 *     0.99 confidence; the consultant has to explicitly accept).
 *   - Strategy hint line under the tag picker shows the resolved
 *     re-eval strategy for the picked tag (pure lookup; not the LLM).
 *   - Save calls `POST /api/v1/overrides` with a fresh idempotency key.
 *     On success the modal closes and the parent's `onSaved` is invoked
 *     with the new override id + the override.captured signal — the
 *     parent is responsible for opening the re-eval tray (the
 *     acceptance criterion's round-trip).
 *
 * Stay-out zones (T-0113):
 *   - We do NOT compute confidence / posture — we surface what the
 *     parent passes (R6 invariant — no LLM-generated numbers).
 *   - We do NOT validate the new_value's shape — that's the calling
 *     register's responsibility before opening this modal.
 *
 * Dismiss rules:
 *   - ESC closes (Radix).
 *   - Click-outside dismisses when no save is in flight.
 *   - Mid-save: dismiss is suppressed so the user can't lose the
 *     in-flight write.
 */

export interface OverrideDiffField {
  /** Display label, e.g. "Severity". */
  label: string;
  /** Prior (AI-proposed or current) value as a human-readable string. */
  before: string;
  /** New (consultant edit) value. */
  after: string;
  /** Optional movement label rendered next to the new value. */
  movement?: string;
}

export interface OverrideTargetSummary {
  /** Short identifier shown in the modal head. e.g. "R-018". */
  shortId?: string;
  /** Title — the entry's primary descriptor. */
  title: string;
  /** Optional domain / register label, e.g. "Cutover". */
  domain?: string;
  /** Optional original AI confidence (for the AI proposal side of the diff). */
  originalAiConfidence?: number | null;
  /**
   * Whether this entry is a baseline-forming item per E02-F06. When true,
   * the no-auto-accept banner renders inside the modal head.
   */
  baselineForming?: boolean;
}

export interface OverrideModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Engagement id — used for cache invalidation on successful capture. */
  engagementId: string;
  /** The register entry id under override. */
  registerEntryId: string;
  /** Optional field path being overridden (null = whole-entry). */
  fieldId?: string | null;
  /** Optional source document id (carried into the event payload). */
  sourceDocumentId?: string | null;
  /** Human-readable summary of the entry being edited. */
  target: OverrideTargetSummary;
  /** Side-by-side diff rows. */
  diff: OverrideDiffField[];
  /** The structured new-value payload to write. */
  newValue: Record<string, unknown>;
  /**
   * Called after the override commits successfully — gives the parent
   * the saved id + the original payload it can stash. The parent is
   * responsible for opening the re-eval tray.
   */
  onSaved: (result: CaptureOverrideResult & { reasonTag: string; rationale: string }) => void;
}

export function OverrideModal({
  open,
  onOpenChange,
  engagementId,
  registerEntryId,
  fieldId = null,
  sourceDocumentId = null,
  target,
  diff,
  newValue,
  onSaved,
}: OverrideModalProps) {
  const taxonomy = useOverrideTaxonomyQuery();
  const capture = useCaptureOverrideMutation(engagementId);
  const titleId = useId();
  const rationaleId = useId();

  const [reasonTag, setReasonTag] = useState<string | null>(null);
  const [rationale, setRationale] = useState('');
  // The save button shows red field hints AFTER the user attempts to
  // save with empty fields. Pre-submit, only the disabled state hints
  // at validity — matches the prototype's two-stage validation feel.
  const [showValidation, setShowValidation] = useState(false);

  // Reset draft state every time the modal opens. Prevents the prior
  // draft from leaking when the same modal is reused for a different
  // entry.
  useEffect(() => {
    if (open) {
      setReasonTag(null);
      setRationale('');
      setShowValidation(false);
      capture.reset();
    }
  }, [open, registerEntryId, capture]);

  const trimmed = rationale.trim();
  const rationaleOk = trimmed.length >= 10;
  const tagOk = reasonTag !== null;
  const canSave = rationaleOk && tagOk && !capture.isPending;
  const pickedTag = useMemo<OverrideTaxonomyTag | undefined>(
    () => taxonomy.data?.tags.find((t) => t.key === reasonTag),
    [reasonTag, taxonomy.data?.tags],
  );
  const strategyHint = reasonTag ? strategyHintForTag(reasonTag) : null;

  async function onSubmit() {
    if (!tagOk || !rationaleOk) {
      setShowValidation(true);
      return;
    }
    try {
      const result = await capture.mutateAsync({
        register_entry_id: registerEntryId,
        field_id: fieldId,
        reason_tag: reasonTag!,
        rationale: trimmed,
        new_value: newValue,
        source_document_id: sourceDocumentId,
      });
      onSaved({
        ...result,
        reasonTag: reasonTag!,
        rationale: trimmed,
      });
    } catch {
      // Mutation error surfaces below the save row; we don't throw out
      // of the handler so the modal stays open with the validation hint.
    }
  }

  const saveErrorMessage = (() => {
    const err = capture.error;
    if (!err) return null;
    if (err instanceof ApiError) return `${err.code}: ${err.message}`;
    if (err instanceof Error) return err.message;
    return 'Save failed — try again.';
  })();

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        // Suppress the implicit close while a save is in flight.
        if (!o && capture.isPending) return;
        onOpenChange(o);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-scrim',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        />
        <Dialog.Content
          aria-labelledby={titleId}
          onPointerDownOutside={(e) => {
            if (capture.isPending) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (capture.isPending) e.preventDefault();
          }}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 grid w-[min(820px,94vw)] max-h-[88vh]',
            '-translate-x-1/2 -translate-y-1/2 grid-rows-[auto,1fr,auto]',
            'overflow-hidden rounded-modal bg-paper-0 shadow-modal',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        >
          <header className="flex items-start justify-between gap-3 border-b border-ink-100 px-6 py-4 pr-14">
            <div className="min-w-0">
              <div className="text-meta uppercase tracking-wide text-ink-500">
                Override
                {target.shortId ? <> · {target.shortId}</> : null}
                {target.domain ? <> · {target.domain}</> : null}
              </div>
              <Dialog.Title id={titleId} className="text-h2 font-semibold text-ink-900">
                {target.title}
              </Dialog.Title>
              <p className="mt-1 text-meta text-ink-500">
                Pick a reason tag and add rationale (≥1 sentence). AI re-evaluation will run per tag
                strategy.
              </p>
            </div>
            <Dialog.Close
              aria-label="Close override modal"
              disabled={capture.isPending}
              className={cn(
                'absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-card',
                'text-ink-500 hover:bg-paper-100 hover:text-ink-900',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
                capture.isPending && 'cursor-not-allowed opacity-50',
              )}
            >
              <XIcon className="h-4 w-4" aria-hidden="true" />
            </Dialog.Close>
          </header>

          <div className="overflow-y-auto px-6 py-5">
            {target.baselineForming ? <NoAutoAcceptBanner /> : null}

            <DiffView diff={diff} originalAiConfidence={target.originalAiConfidence ?? null} />

            <hr className="my-5 border-ink-100" />

            <fieldset className="space-y-4">
              <legend className="sr-only">Override reason and rationale</legend>

              <div>
                <div className="flex items-baseline justify-between">
                  <label className="text-body-sm font-semibold text-ink-900">
                    Reason tag{' '}
                    <span className="ml-1 text-meta font-medium text-ink-500">
                      · required · v1 taxonomy
                    </span>
                  </label>
                  <span className="text-meta text-ink-500">
                    {taxonomy.isLoading
                      ? 'Loading taxonomy…'
                      : taxonomy.error
                        ? 'Taxonomy unavailable — retry later'
                        : `${taxonomy.data?.tags.length ?? 0} tags`}
                  </span>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {taxonomy.isLoading ? (
                    <span className="inline-flex items-center gap-1.5 text-meta text-ink-500">
                      <Spinner /> Loading…
                    </span>
                  ) : null}
                  {taxonomy.data?.tags.map((t) => {
                    const active = reasonTag === t.key;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setReasonTag(t.key)}
                        title={t.description}
                        aria-pressed={active}
                        className={cn(
                          'inline-flex items-center rounded-full border px-3 py-1 text-meta font-medium transition-colors',
                          active
                            ? 'border-accent-amber-600 bg-accent-amber-50 text-accent-amber-700'
                            : 'border-ink-200 bg-paper-0 text-ink-700 hover:border-ink-300',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
                        )}
                      >
                        {t.label}
                      </button>
                    );
                  })}
                </div>
                {showValidation && !tagOk ? (
                  <p
                    role="alert"
                    className="mt-1.5 inline-flex items-center gap-1 text-meta text-posture-critical"
                  >
                    <AlertTriangle className="h-3 w-3" aria-hidden="true" /> Pick a reason tag.
                  </p>
                ) : null}
                {pickedTag && strategyHint ? (
                  <div className="mt-2 inline-flex items-start gap-2 rounded-card border border-ai-surface-border bg-ai-surface px-3 py-2 text-meta text-ai-ink">
                    <Zap className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
                    <div>
                      <span className="font-semibold text-ai-ink">{pickedTag.label}:</span>{' '}
                      {strategyHint.label}
                    </div>
                  </div>
                ) : null}
              </div>

              <div>
                <label htmlFor={rationaleId} className="text-body-sm font-semibold text-ink-900">
                  Rationale{' '}
                  <span className="ml-1 text-meta font-medium text-ink-500">
                    · required · ≥1 sentence (≥10 characters)
                  </span>
                </label>
                <textarea
                  id={rationaleId}
                  rows={3}
                  value={rationale}
                  onChange={(e) => setRationale(e.target.value)}
                  onBlur={() => setShowValidation(true)}
                  placeholder="Describe why you're changing this value. Cite recent evidence, interviews, or methodology context."
                  className={cn(
                    'mt-1.5 w-full resize-y rounded-card border bg-paper-0 px-3 py-2 text-body text-ink-900',
                    'placeholder:text-ink-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
                    showValidation && !rationaleOk ? 'border-posture-critical' : 'border-ink-200',
                  )}
                />
                <div className="mt-1 flex items-center justify-between text-meta text-ink-500">
                  <span>
                    {trimmed.length} char{trimmed.length === 1 ? '' : 's'}
                    {rationaleOk ? (
                      <span className="ml-1 text-posture-healthy">· ok</span>
                    ) : (
                      <span className="ml-1">· need ≥10 chars</span>
                    )}
                  </span>
                  {showValidation && !rationaleOk ? (
                    <span className="inline-flex items-center gap-1 text-posture-critical">
                      <AlertTriangle className="h-3 w-3" aria-hidden="true" /> Add at least one
                      sentence.
                    </span>
                  ) : null}
                </div>
              </div>
            </fieldset>

            {saveErrorMessage ? (
              <p
                role="alert"
                className="mt-3 rounded-card border border-posture-critical/40 bg-posture-critical-tint px-3 py-2 text-meta text-posture-critical"
              >
                {saveErrorMessage}
              </p>
            ) : null}
          </div>

          <footer className="flex items-center justify-between gap-3 border-t border-ink-100 bg-paper-50 px-6 py-3">
            <div className="flex items-center gap-2 text-meta text-ink-500">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
              Audit log entry will be created
              {pickedTag ? (
                <>
                  {' '}
                  · tagged <strong>{pickedTag.label}</strong>
                </>
              ) : null}
              .
            </div>
            <div className="flex items-center gap-2">
              <Dialog.Close
                disabled={capture.isPending}
                className={cn(
                  'inline-flex h-9 items-center rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
                  'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
                  capture.isPending && 'cursor-not-allowed opacity-50',
                )}
              >
                Cancel
              </Dialog.Close>
              <button
                type="button"
                disabled={!canSave}
                onClick={onSubmit}
                className={cn(
                  'inline-flex h-9 items-center gap-1.5 rounded-card bg-accent-amber-600 px-4 text-body-sm font-semibold text-paper-0',
                  'hover:bg-accent-amber-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
              >
                {capture.isPending ? (
                  <>
                    <Spinner className="text-paper-0" /> Saving…
                  </>
                ) : (
                  'Save override'
                )}
              </button>
            </div>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * No-auto-accept banner — surfaces when the entry being overridden is
 * baseline-forming per E02-F06. The product rule is "even at 0.99
 * confidence, baseline-forming items require explicit acceptance".
 */
function NoAutoAcceptBanner() {
  return (
    <div
      role="status"
      className="mb-4 flex items-start gap-3 rounded-card border-l-2 border-accent-amber-600 bg-accent-amber-50 px-4 py-3"
    >
      <Zap className="mt-0.5 h-4 w-4 shrink-0 text-accent-amber-700" aria-hidden="true" />
      <div className="text-body-sm text-ink-700">
        <strong className="font-semibold text-ink-900">No-auto-accept zone.</strong> This is a
        baseline-forming item — AI proposals here never auto-apply, even at 0.99 confidence. Your
        override sets the canonical value going forward.
      </div>
    </div>
  );
}

/**
 * Side-by-side AI-proposal / final-edit grid. Mirrors the prototype's
 * `.diff` rule (modals.js render → "AI proposal" vs "Final · consultant
 * edit"). The values are passed as opaque strings so any register type
 * can use this modal.
 */
function DiffView({
  diff,
  originalAiConfidence,
}: {
  diff: OverrideDiffField[];
  originalAiConfidence: number | null;
}) {
  if (diff.length === 0) {
    return (
      <p className="rounded-card border border-dashed border-ink-200 bg-paper-50 px-4 py-3 text-meta text-ink-500">
        No diff provided — the parent surface didn&apos;t pass a before/after value list. The
        override still records the new payload, but the modal can&apos;t show the comparison.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-[1fr,auto,1fr] items-stretch gap-3">
      <div className="rounded-card border border-ai-surface-border bg-ai-surface p-3">
        <div className="text-meta uppercase tracking-wide text-ai-ink">AI proposal</div>
        <dl className="mt-2 grid grid-cols-[max-content,1fr] gap-x-3 gap-y-1.5 text-body-sm">
          {diff.map((d) => (
            <DiffPair key={`before-${d.label}`} label={d.label} value={d.before} />
          ))}
          {originalAiConfidence != null ? (
            <>
              <dt className="text-ink-500">Confidence</dt>
              <dd className="font-mono text-ink-700">
                <AiConfidenceChip value={originalAiConfidence} />
              </dd>
            </>
          ) : null}
        </dl>
      </div>
      <div aria-hidden="true" className="flex items-center justify-center pt-6 text-ink-300">
        <ArrowRight className="h-5 w-5" />
      </div>
      <div className="rounded-card border border-accent-amber-600/40 bg-accent-amber-50 p-3">
        <div className="text-meta uppercase tracking-wide text-accent-amber-700">
          Final · consultant edit
        </div>
        <dl className="mt-2 grid grid-cols-[max-content,1fr] gap-x-3 gap-y-1.5 text-body-sm">
          {diff.map((d) => (
            <DiffPair
              key={`after-${d.label}`}
              label={d.label}
              value={d.after}
              movement={d.movement}
            />
          ))}
          <dt className="text-ink-500">Provenance</dt>
          <dd>
            <ProvenanceChip origin="consultant_edited" />
          </dd>
        </dl>
      </div>
    </div>
  );
}

function DiffPair({ label, value, movement }: { label: string; value: string; movement?: string }) {
  return (
    <>
      <dt className="text-ink-500">{label}</dt>
      <dd className="text-ink-900">
        {value}
        {movement ? (
          <span className="ml-1.5 text-meta text-posture-at-risk">{movement}</span>
        ) : null}
      </dd>
    </>
  );
}
